/**
 * Файл на телефоне (T-205, T-174): не новая вкладка и не переход по ссылке — на iPhone
 * (особенно в приложении с экрана «Домой») вкладка с файлом остаётся пустой, а со страницы
 * файла нет пути назад. Вместо этого — меню «Поделиться»: WhatsApp, Telegram, почта, печать.
 */

import { androidApp } from '@/runtime';

/** iPhone и iPad (iPad Safari представляется Mac-ом с сенсорным экраном). */
export function isApple(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1)
  );
}

/**
 * Файл — через «Поделиться», а не новой вкладкой: телефон, iPhone/iPad и приложение для
 * Android (в WebView нет вкладок, а ссылки blob: не скачиваются — даже на планшете).
 */
export function shareFirst(mobile: boolean): boolean {
  return mobile || isApple() || androidApp() !== null;
}

/** Можно ли отдать файл в меню «Поделиться» (Chrome на Android, Safari на iPhone, приложение). */
export function canShareFile(file: File): boolean {
  if (androidApp()) return true;
  return typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
}

/**
 * Файл → base64 для моста приложения Android (строкой — другого способа передать нет).
 * FileReader, а не file.arrayBuffer(): его нет в старых WebView на старых Android.
 */
function base64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = typeof reader.result === 'string' ? reader.result : '';
      resolve(url.slice(url.indexOf(',') + 1)); // «data:…;base64,» — отрезаем
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('файл не прочитан'));
    };
    reader.readAsDataURL(file);
  });
}

/** Меню «Поделиться» с файлом: false — меню недоступно; закрыли меню — это не ошибка. */
export async function shareFile(file: File): Promise<boolean> {
  const android = androidApp();
  if (android) {
    android.shareFile(await base64(file), file.name, file.type || 'application/octet-stream');
    return true;
  }
  try {
    await navigator.share({ files: [file], title: file.name });
  } catch (e) {
    return e instanceof DOMException && e.name === 'AbortError';
  }
  return true;
}

/** Сохранить файл (в «Загрузки» телефона или компьютера). */
export function saveFile(file: File): void {
  const android = androidApp();
  if (android) {
    void base64(file).then((data) => {
      android.saveFile(data, file.name, file.type || 'application/octet-stream');
    });
    return;
  }
  const url = URL.createObjectURL(file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
}
